'use client';

import CommentTrigger from '@/components/icons/comment-trigger';
import { cn } from '@/lib/utils';
import { isMobileViewport } from '../../utils/is-mobile-viewport.util';
import { useParagraphCommentUiContext } from './paragraph-comment-ui-context';

type ReaderParagraphProps = {
  paragraphId: string;
  index: number;
  textColor: string;
  paragraphText: string;
  children: React.ReactNode;
};

export function ReaderParagraph({
  paragraphId,
  index,
  textColor,
  paragraphText,
  children,
}: ReaderParagraphProps) {
  const ui = useParagraphCommentUiContext();
  const count = ui?.getParagraphCommentCount?.(paragraphId) ?? 0;
  const isSelected = ui?.selectedParagraphId === paragraphId;
  const isRevealed = ui?.revealedParagraphId === paragraphId;
  const canComment = Boolean(ui?.onCommentIconClick && paragraphText.trim());

  const handleParagraphClick = (event: React.MouseEvent) => {
    if (!canComment) return;
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.toString().trim()) return;
    if (!isMobileViewport()) return;
    event.stopPropagation();
    ui?.onRevealParagraph(paragraphId);
  };

  const handleIconClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canComment || !ui?.onCommentIconClick) return;
    ui.onCommentIconClick(paragraphId, paragraphText, index);
  };

  return (
    <div
      className={cn(
        'group relative mb-4 flex items-end gap-2',
        isSelected &&
          'rounded-lg bg-[#F7F7F7] px-1 -mx-1 group-data-[reader-tone=dark]/reader:!bg-white/[0.08]',
      )}
    >
      <p
        id={paragraphId}
        data-paragraph-id={paragraphId}
        data-paragraph-index={index}
        className={cn(
          'min-w-0 flex-1 break-words leading-[inherit] [overflow-wrap:anywhere]',
          canComment && 'cursor-pointer md:cursor-default',
        )}
        style={{ color: textColor }}
        onClick={handleParagraphClick}
      >
        {children}
      </p>
      {canComment && (
        <button
          type="button"
          data-comment-trigger={paragraphId}
          onClick={handleIconClick}
          title={count > 0 ? `Комментарии (${count})` : 'Добавить комментарий'}
          className={cn(
            // leading-7 (28px) vs icon 26px → +1px снизу, чтобы центр иконки = центр последней строки
            'mb-px shrink-0 origin-center transition-[opacity,transform] duration-150 hover:scale-105',
            'md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100',
            isRevealed ? 'opacity-100' : 'max-md:opacity-0',
          )}
        >
          <CommentTrigger count={count} />
        </button>
      )}
    </div>
  );
}
